import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutDashboard, DollarSign, Users, LogOut, Menu, Copy, Check, Gift, TrendingUp, Wallet, ArrowRight, Share2, ShoppingBag, Award, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { ENLogo } from '@/components/ENLogo';
import { MobileBottomNav } from '@/components/MobileNav';
import { useIsMobile } from '@/hooks/useIsMobile';
import { toast } from 'sonner';
import { publicUrl } from '@/lib/publicUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupporterDashboard() {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [referralInfo, setReferralInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [merchCatalog, setMerchCatalog] = useState([]);
  const [redeemingItem, setRedeemingItem] = useState(null);
  const [platformRedeemAmount, setPlatformRedeemAmount] = useState('');
  const [requestingPayout, setRequestingPayout] = useState(false);

  useEffect(() => {
    fetchReferralInfo();
    fetchMerchCatalog();
  }, []);

  const fetchReferralInfo = async () => {
    try {
      const response = await fetch(`${API}/supporters/my-referral-info`, {
        headers: getAuthHeaders()
      });
      if (response.ok) setReferralInfo(await response.json());
    } catch (error) { console.error('Failed to fetch referral info:', error); }
    finally { setLoading(false); }
  };

  const fetchMerchCatalog = async () => {
    try {
      const response = await fetch(`${API}/supporters/merch-catalog`);
      if (response.ok) setMerchCatalog(await response.json());
    } catch (error) { console.error('Failed to fetch merch catalog:', error); }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const copyPromoCode = () => {
    if (referralInfo?.promo_code) {
      navigator.clipboard.writeText(referralInfo.promo_code);
      setCopied(true);
      toast.success('Promo code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyReferralLink = () => {
    const link = publicUrl(`/browse?ref=${referralInfo?.promo_code}`);
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  const handleRedeemMerch = async (item) => {
    setRedeemingItem(item.item_id);
    try {
      const response = await fetch(`${API}/supporters/redeem-credits`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'merch', item_id: item.item_id })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        fetchReferralInfo();
      } else {
        toast.error(data.detail || 'Redemption failed');
      }
    } catch (error) { toast.error('Redemption failed'); }
    finally { setRedeemingItem(null); }
  };

  const handleRedeemPlatform = async () => {
    const amount = parseFloat(platformRedeemAmount);
    if (!amount || amount <= 0) return toast.error('Enter a valid amount');
    try {
      const response = await fetch(`${API}/supporters/redeem-credits`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'platform', amount, apply_to: 'account' })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setPlatformRedeemAmount('');
        fetchReferralInfo();
      } else {
        toast.error(data.detail || 'Redemption failed');
      }
    } catch (error) { toast.error('Redemption failed'); }
  };

  const handleRequestPayout = async () => {
    setRequestingPayout(true);
    try {
      const response = await fetch(`${API}/supporters/request-payout`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (response.ok) { toast.success(data.message); fetchReferralInfo(); }
      else { toast.error(data.detail || 'Failed to request payout'); }
    } catch (error) { toast.error(error.message); }
    finally { setRequestingPayout(false); }
  };

  const navItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'redeem', label: 'Redeem Credits', icon: Gift },
    { id: 'merch', label: 'Merch Catalog', icon: ShoppingBag },
    { id: 'history', label: 'History', icon: TrendingUp },
  ];

  const isMobile = useIsMobile();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E0E23] via-[#1A1A3E] to-[#0E0E23] text-white flex" data-testid="supporter-dashboard">
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(139,92,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      {/* Sidebar — desktop only */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#12122A]/90 backdrop-blur-xl border-r border-white/5 transform transition-transform lg:translate-x-0 hidden lg:block`}>
        <div className="flex flex-col h-full relative z-10">
          <div className="p-6 border-b border-white/5">
            <ENLogo size="sm" linkTo="" />
          </div>
          <div className="p-6 border-b border-white/5">
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center">
                  <span className="text-[#A78BFA] font-syne font-bold">{user?.name?.[0]}</span>
                </div>
              )}
              <div>
                <p className="text-white font-syne font-medium truncate text-sm">{user?.name}</p>
                <p className="text-[#A78BFA] text-xs font-syne">Supporter</p>
              </div>
            </div>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map(item => (
              <button key={item.id} onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-syne text-sm transition-all ${activeTab === item.id ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border border-[#8B5CF6]/20' : 'text-[#9CA3AF] hover:text-white hover:bg-white/5'}`}>
                <item.icon className="w-5 h-5" />{item.label}
              </button>
            ))}
            <Link to="/browse" className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#9CA3AF] hover:text-white hover:bg-white/5 font-syne text-sm">
              <Share2 className="w-5 h-5" />Browse Vendors
            </Link>
          </nav>
          <div className="p-4 border-t border-white/5">
            <Button onClick={handleLogout} variant="ghost" className="w-full justify-start text-[#9CA3AF] hover:text-red-400 font-syne text-sm" data-testid="supporter-logout-btn">
              <LogOut className="w-5 h-5 mr-3" /> Logout
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen relative z-10">
        <main className="flex-1 p-4 pb-24 lg:p-8 lg:pb-8">
          <div className="space-y-8 max-w-5xl mx-auto">
            <div>
              <h1 className="font-syne text-2xl md:text-3xl font-bold text-white mb-2" data-testid="supporter-dashboard-title">
                Supporter Dashboard
              </h1>
              <p className="text-[#9CA3AF] font-syne text-sm">Earn Reward Credits by promoting Indigenous businesses</p>
            </div>

            {activeTab === 'overview' && (
              <>
                {/* Promo Code Card */}
                <motion.div className="bg-[#2D2D52]/40 backdrop-blur-xl border border-[#8B5CF6]/20 rounded-2xl p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="promo-code-card">
                  <div className="flex items-center gap-3 mb-4">
                    <Gift className="w-6 h-6 text-[#A78BFA]" />
                    <h2 className="font-syne text-lg font-bold text-white">Your Promo Code</h2>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 p-4 rounded-xl bg-[#0E0E23]/80 border border-white/5 font-mono text-2xl text-[#A78BFA] text-center tracking-widest font-bold" data-testid="promo-code-display">
                      {referralInfo?.promo_code || '--------'}
                    </div>
                    <Button onClick={copyPromoCode} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-bold h-14 px-6" data-testid="copy-promo-code">
                      {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={copyReferralLink} variant="outline" className="flex-1 border-white/10 text-[#9CA3AF] hover:text-white hover:border-[#8B5CF6]/30 text-sm" data-testid="copy-referral-link">
                      <Share2 className="w-4 h-4 mr-2" />Copy Referral Link
                    </Button>
                  </div>
                  <p className="text-[#64748B] text-xs mt-3">Earn 3% Reward Credits when someone books using your code</p>
                </motion.div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: 'Reward Credits', value: `${(referralInfo?.reward_credits || 0).toFixed(2)}`, icon: Award, color: 'text-[#A78BFA]' },
                    { label: 'Total Earned', value: `${(referralInfo?.total_credits_earned || 0).toFixed(2)}`, icon: TrendingUp, color: 'text-green-400' },
                    { label: 'Total Redeemed', value: `${(referralInfo?.total_credits_redeemed || 0).toFixed(2)}`, icon: Wallet, color: 'text-blue-400' },
                    { label: 'Referrals', value: referralInfo?.referral_count || 0, icon: Users, color: 'text-[#A78BFA]' },
                  ].map((stat, i) => (
                    <motion.div key={i} className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-2xl p-5"
                      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                      data-testid={`stat-${stat.label.toLowerCase().replace(/ /g, '-')}`}>
                      <stat.icon className={`w-8 h-8 ${stat.color} mb-3`} />
                      <span className="block text-2xl font-syne font-bold text-white">{stat.value}</span>
                      <span className="text-[#64748B] text-xs font-syne">{stat.label}</span>
                    </motion.div>
                  ))}
                </div>

                {/* How to Earn */}
                <div className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-2xl p-6">
                  <h2 className="font-syne text-lg font-bold text-white mb-4">How to Earn Reward Credits</h2>
                  <div className="space-y-4">
                    {[
                      { num: '1', title: 'Share Indigenous Business Pages', desc: 'Browse vendors and share their profiles with your network', color: 'bg-[#8B5CF6]' },
                      { num: '2', title: 'Customer Books a Service', desc: 'When they book, they enter your promo code at checkout', color: 'bg-[#8B5CF6]' },
                      { num: '3', title: 'Transaction Completes', desc: 'After escrow releases, your credits are automatically awarded', color: 'bg-[#8B5CF6]' },
                      { num: '$', title: 'Redeem Your Credits!', desc: 'Use credits for subscriptions, merch, or optional cash redemption', color: 'bg-green-500' },
                    ].map((step, i) => (
                      <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-[#0E0E23]/40 border border-white/5">
                        <div className={`w-8 h-8 rounded-full ${step.color} flex items-center justify-center text-white font-bold flex-shrink-0 text-sm`}>{step.num}</div>
                        <div>
                          <p className="text-white font-syne font-medium text-sm">{step.title}</p>
                          <p className="text-[#64748B] text-xs font-syne">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'redeem' && (
              <>
                {/* Platform Credit Redemption */}
                <motion.div className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-2xl p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="platform-redeem-section">
                  <h2 className="font-syne text-lg font-bold text-white mb-2">Apply Credits to Platform</h2>
                  <p className="text-[#64748B] text-sm mb-4">Use credits toward subscriptions, tools, or services (min 10 credits)</p>
                  <div className="flex gap-3">
                    <input type="number" value={platformRedeemAmount} onChange={(e) => setPlatformRedeemAmount(e.target.value)}
                      className="flex-1 bg-[#0E0E23]/80 border border-white/10 rounded-xl px-4 py-3 text-white font-mono focus:border-[#8B5CF6]/50 focus:outline-none" placeholder="Amount"
                      data-testid="platform-redeem-amount" />
                    <Button onClick={handleRedeemPlatform} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-bold px-6" data-testid="platform-redeem-btn">
                      Redeem
                    </Button>
                  </div>
                  <p className="text-[#64748B] text-xs mt-2">Available: {(referralInfo?.reward_credits || 0).toFixed(2)} credits</p>
                </motion.div>

                {/* Cash Redemption (Optional) */}
                <motion.div className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-2xl p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} data-testid="cash-redeem-section">
                  <h2 className="font-syne text-lg font-bold text-white mb-2">Optional Cash Redemption</h2>
                  <p className="text-[#64748B] text-sm mb-4">Convert credits to cash (min ${referralInfo?.minimum_cash_payout || 100} balance, verification required)</p>
                  <div className="p-4 rounded-xl bg-[#0E0E23]/40 border border-white/5 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[#9CA3AF] text-sm font-syne">Available for Cash Payout</span>
                      <span className="text-green-400 font-mono text-xl font-bold">{(referralInfo?.pending_payout || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#64748B] font-syne">Minimum Threshold</span>
                      <span className="text-[#9CA3AF] font-mono">${referralInfo?.minimum_cash_payout || 100}</span>
                    </div>
                  </div>
                  {referralInfo?.can_request_cash_payout ? (
                    <Button onClick={handleRequestPayout} disabled={requestingPayout}
                      className="w-full bg-green-600 hover:bg-green-500 text-white font-bold" data-testid="request-payout-btn">
                      {requestingPayout ? 'Processing...' : 'Request Promotional Reward Payout'}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  ) : (
                    <div className="text-center p-4 rounded-xl bg-[#0E0E23]/40 border border-white/5">
                      <p className="text-[#64748B] text-sm">Need {((referralInfo?.minimum_cash_payout || 100) - (referralInfo?.pending_payout || 0)).toFixed(2)} more credits for cash redemption</p>
                    </div>
                  )}
                  <p className="text-[#64748B] text-xs mt-3 text-center">Paid as "Promotional Reward Payout" via Stripe. Processing: 5-10 business days.</p>
                </motion.div>
              </>
            )}

            {activeTab === 'merch' && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <h2 className="font-syne text-lg font-bold text-white mb-2">Merchandise Catalog</h2>
                <p className="text-[#64748B] text-sm mb-6">Redeem your Reward Credits for official EntrepreNatives merch</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {merchCatalog.map(item => (
                    <div key={item.item_id} className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-2xl p-5 flex flex-col" data-testid={`merch-item-${item.item_id}`}>
                      <div className="w-full h-32 rounded-xl bg-[#0E0E23]/60 border border-white/5 flex items-center justify-center mb-4">
                        <ShoppingBag className="w-10 h-10 text-[#8B5CF6]/40" />
                      </div>
                      <h3 className="font-syne font-bold text-white text-sm">{item.name}</h3>
                      <p className="text-[#64748B] text-xs mb-3">{item.description}</p>
                      <div className="mt-auto flex items-center justify-between">
                        <span className="text-[#A78BFA] font-mono font-bold">{item.credit_cost} credits</span>
                        <Button onClick={() => handleRedeemMerch(item)} disabled={redeemingItem === item.item_id || (referralInfo?.reward_credits || 0) < item.credit_cost}
                          className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs px-4 py-2 h-auto disabled:opacity-40" data-testid={`redeem-merch-${item.item_id}`}>
                          {redeemingItem === item.item_id ? '...' : 'Redeem'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                {merchCatalog.length === 0 && (
                  <div className="text-center py-12 text-[#64748B]">Loading catalog...</div>
                )}
              </motion.div>
            )}

            {activeTab === 'history' && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                {/* Referral History */}
                <div className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-2xl p-6 mb-6">
                  <h2 className="font-syne text-lg font-bold text-white mb-4">Referral History</h2>
                  {referralInfo?.referral_history?.length > 0 ? (
                    <div className="space-y-3">
                      {referralInfo.referral_history.map((ref) => (
                        <div key={ref.referral_id} className="p-4 rounded-xl bg-[#0E0E23]/40 border border-white/5 flex items-center justify-between" data-testid={`referral-${ref.referral_id}`}>
                          <div>
                            <p className="text-white font-syne text-sm">{ref.referred_user_name}</p>
                            <p className="text-[#64748B] text-xs">{new Date(ref.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="text-right">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${ref.status === 'completed' ? 'bg-green-500/20 text-green-400' : ref.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>
                              {ref.status}
                            </span>
                            <p className="text-[#A78BFA] font-mono font-bold mt-1 text-sm">+{(ref.reward_credits || ref.reward_amount || 0).toFixed(2)} credits</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#64748B] text-sm text-center py-6">No referrals yet. Share your promo code to start earning!</p>
                  )}
                </div>

                {/* Redemption History */}
                <div className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-2xl p-6">
                  <h2 className="font-syne text-lg font-bold text-white mb-4">Redemption History</h2>
                  {referralInfo?.redemption_history?.length > 0 ? (
                    <div className="space-y-3">
                      {referralInfo.redemption_history.map((rdm) => (
                        <div key={rdm.redemption_id} className="p-4 rounded-xl bg-[#0E0E23]/40 border border-white/5 flex items-center justify-between" data-testid={`redemption-${rdm.redemption_id}`}>
                          <div>
                            <p className="text-white font-syne text-sm">{rdm.description}</p>
                            <p className="text-[#64748B] text-xs">{new Date(rdm.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="text-right">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${rdm.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                              {rdm.status}
                            </span>
                            <p className="text-red-400 font-mono font-bold mt-1 text-sm">-{rdm.amount.toFixed(2)} credits</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#64748B] text-sm text-center py-6">No redemptions yet.</p>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </main>

        {/* Mobile Tab Switcher */}
        {isMobile && (
          <nav className="fixed bottom-0 left-0 right-0 z-50 bg-[#0E0E23]/95 backdrop-blur-2xl border-t border-white/10" data-testid="supporter-mobile-nav">
            <div className="flex items-center justify-around px-1 pt-2 pb-3">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className="flex flex-col items-center gap-0.5 min-w-0 flex-1"
                  data-testid={`mobile-nav-${item.id}`}
                >
                  <item.icon size={20} strokeWidth={activeTab === item.id ? 2.2 : 1.5} className={activeTab === item.id ? 'text-[#A78BFA]' : 'text-[#64748B]'} />
                  <span className={`text-[10px] font-medium truncate ${activeTab === item.id ? 'text-[#A78BFA]' : 'text-[#64748B]'}`}>{item.label}</span>
                </button>
              ))}
            </div>
          </nav>
        )}
      </div>
    </div>
  );
}
